import { useState } from 'react'
import { Gift } from 'lucide-react'

import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { toast } from 'sonner'

import useQueryGetDepositPromotion from '@/modules/promotion/api/hooks/useQueryGetDepositPromotion'
import useMutationBuildPromotionFlex from '@/modules/messaging/api/hooks/useMutationBuildPromotionFlex'
import { ChatMessageType, type components } from '@/schema'

type ChatMessageContentDto = components['schemas']['ChatMessageContentDto']

interface InstantCreditClaimDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSendMessage: (message: ChatMessageContentDto) => void
}

export function InstantCreditClaimDialog({
	open,
	onOpenChange,
	onSendMessage,
}: InstantCreditClaimDialogProps) {
	const [selectedPromotionId, setSelectedPromotionId] = useState<string>('')

	const { data: promotions, isLoading } = useQueryGetDepositPromotion({
		skip: 0,
		take: 100,
	})

	// Filter promotions that allow instant claim
	const instantClaimPromotions =
		promotions?.data?.filter((p) => p.allowInstantClaim && p.isActive) || []

	const selectedPromotion = instantClaimPromotions.find(
		(p) => p.id === selectedPromotionId,
	)

	const buildPromotionFlexMutation = useMutationBuildPromotionFlex({
		onSuccess: (data) => {
			if (!data?.flexJson) {
				toast.error('ไม่สามารถสร้างข้อความได้')
				return
			}

			// Send the flex message
			const messageData: ChatMessageContentDto = {
				clientId: crypto.randomUUID(),
				messageType: 'FLEX' as ChatMessageType,
				order: 0,
				flexAltText: `🎁 ${selectedPromotion?.name} - คลิกรับเครดิตฟรี`,
				flexContents: data.flexJson as unknown as Record<string, never>,
			}

			onSendMessage(messageData)
			onOpenChange(false)
			setSelectedPromotionId('')
			toast.success('ส่งลิงก์รับเครดิตฟรีแล้ว')
		},
		onError: (error) => {
			console.error('Failed to build promotion flex:', error)
			toast.error('ส่งข้อความไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})

	const handleSend = () => {
		if (!selectedPromotion) {
			toast.error('กรุณาเลือกโปรโมชั่น')
			return
		}

		// Call backend to build promotion flex message
		buildPromotionFlexMutation.mutate({
			promotionIds: [selectedPromotion.id],
			enableInstantClaim: true,
		})
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Gift className="h-5 w-5" />
						ส่งลิงก์รับเครดิตฟรี
					</DialogTitle>
					<DialogDescription>
						เลือกโปรโมชั่นที่ต้องการส่งให้ผู้เล่น (เฉพาะโปรที่รับฟรีเครดิตได้)
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<label className="text-sm font-medium">โปรโมชั่น</label>
						<Select
							value={selectedPromotionId}
							onValueChange={setSelectedPromotionId}
							disabled={isLoading}
						>
							<SelectTrigger>
								<SelectValue placeholder="เลือกโปรโมชั่น" />
							</SelectTrigger>
							<SelectContent>
								{instantClaimPromotions.length === 0 ? (
									<div className="px-2 py-6 text-center text-sm text-muted-foreground">
										{isLoading
											? 'กำลังโหลด...'
											: 'ไม่มีโปรโมชั่นที่รับฟรีเครดิตได้'}
									</div>
								) : (
									instantClaimPromotions.map((promo) => (
										<SelectItem key={promo.id} value={promo.id}>
											<div className="flex items-center gap-2">
												<Gift className="h-4 w-4" />
												<span>{promo.name}</span>
											</div>
										</SelectItem>
									))
								)}
							</SelectContent>
						</Select>
					</div>

					{selectedPromotion && (
						<div className="rounded-lg border bg-muted/50 p-4">
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium">ชื่อ:</span>{' '}
									{selectedPromotion.name}
								</div>
								{selectedPromotion.shortDescription && (
									<div>
										<span className="font-medium">รายละเอียด:</span>{' '}
										{selectedPromotion.shortDescription}
									</div>
								)}
								<div className="mt-3 rounded bg-primary/10 p-2 text-xs text-primary">
									💡 ผู้เล่นจะได้รับลิงก์ที่คลิกแล้วรับเครดิตฟรีทันที
									ไม่ต้องฝากเงิน
								</div>
							</div>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						type="button"
						disabled={buildPromotionFlexMutation.isPending}
					>
						ยกเลิก
					</Button>
					<Button
						onClick={handleSend}
						disabled={
							!selectedPromotionId ||
							isLoading ||
							buildPromotionFlexMutation.isPending
						}
					>
						<Gift className="mr-2 h-4 w-4" />
						{buildPromotionFlexMutation.isPending ? 'กำลังส่ง...' : 'ส่งลิงก์'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
